import { defineStore } from "pinia";
import { ref, computed, watch, readonly } from "vue";
import type { SupportedLocale } from "@/i18n";
import {
    DEFAULT_LOCALE,
    SUPPORTED_LOCALES,
    setLocale,
    getLanguageName,
} from "@/i18n";

const STORAGE_KEY = "lms-language";

export const useLanguageStore = defineStore("language", () => {
    // State
    const currentLocale = ref<SupportedLocale>(DEFAULT_LOCALE);
    const isInitialized = ref(false);

    // Getters
    const locale = computed(() => currentLocale.value);
    const languageName = computed(() => getLanguageName(currentLocale.value));
    const availableLocales = computed(() => SUPPORTED_LOCALES);
    const isEnglish = computed(() => currentLocale.value === "en");
    const isIndonesian = computed(() => currentLocale.value === "id");

    // Actions
    const setLanguage = (newLocale: SupportedLocale) => {
        if (!SUPPORTED_LOCALES.includes(newLocale)) {
            console.warn(
                `[languageStore] Unsupported locale: "${newLocale}". Falling back to "${DEFAULT_LOCALE}".`,
            );
            newLocale = DEFAULT_LOCALE;
        }

        // Update state
        currentLocale.value = newLocale;

        // Update i18n
        setLocale(newLocale);

        // Persist to localStorage
        saveLanguageToStorage(newLocale);
    };

    const toggleLanguage = () => {
        const currentIndex = SUPPORTED_LOCALES.indexOf(currentLocale.value);
        const nextIndex = (currentIndex + 1) % SUPPORTED_LOCALES.length;
        setLanguage(SUPPORTED_LOCALES[nextIndex]);
    };

    const initializeLanguage = () => {
        // Load language from localStorage
        const savedLocale = loadLanguageFromStorage();
        if (savedLocale) {
            currentLocale.value = savedLocale;
        }

        // Apply initial language
        setLocale(currentLocale.value);

        isInitialized.value = true;
    };

    const resetToDefault = () => {
        setLanguage(DEFAULT_LOCALE);
    };

    // Helper functions
    const saveLanguageToStorage = (localeValue: SupportedLocale) => {
        try {
            localStorage.setItem(STORAGE_KEY, localeValue);
        } catch (error) {
            console.warn(
                "[languageStore] Failed to save language to localStorage:",
                error,
            );
        }
    };

    const loadLanguageFromStorage = (): SupportedLocale | null => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved && SUPPORTED_LOCALES.includes(saved as SupportedLocale)) {
                return saved as SupportedLocale;
            }
            return null;
        } catch (error) {
            console.warn(
                "[languageStore] Failed to load language from localStorage:",
                error,
            );
            return null;
        }
    };

    // Watch for locale changes to sync with i18n
    watch(currentLocale, (newLocale) => {
        if (isInitialized.value) {
            setLocale(newLocale);
        }
    });

    return {
        // State
        currentLocale: readonly(currentLocale),
        isInitialized: readonly(isInitialized),

        // Getters
        locale: readonly(locale),
        languageName: readonly(languageName),
        availableLocales: readonly(availableLocales),
        isEnglish: readonly(isEnglish),
        isIndonesian: readonly(isIndonesian),

        // Actions
        setLanguage,
        toggleLanguage,
        initializeLanguage,
        resetToDefault,
    };
});

// Export types for external use
export type { SupportedLocale };
